import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13 } from "styled-components";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Asset, type Props as AssetProps } from "~/features/craft/asset";
import { takeFirst } from "~/features/app/utils";
import { getThemeStyle, type Mode } from "~/features/ui/get-theme-style";
import { Link } from "~/features/ui/link";
import { IconButton } from "~/features/ui/icon-button";
import { type Ratio } from "~/features/ui/aspect-ratio";
import { Tag } from "~/features/ui/tag";
import { hardwareSpecsFragment } from "~/features/blocks/inventory/hardware-comparison-table";
import ArrowRight from "~/public/icons/arrow-right.svg";
const fragment = graphql(`
  fragment ProductCard on hardware_default_Entry {
    id
    title
    url
    productCaption
    productImage {
      ...Asset
    }
    ...HardwareSpecs
  }
`);
export type ProductCardViewProps = {
  asset?: AssetProps["asset"];
  id?: string | null;
  mode: Mode;
  productCaption?: string | null;
  productHighlights?: string[] | null;
  ratio?: Ratio;
  title?: string | null;
  url?: string | null;
};
export function ProductCardView({
  asset,
  id,
  mode = "light",
  productCaption,
  productHighlights,
  ratio = "496/540",
  title,
  url
}: ProductCardViewProps) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode
  });
  const hoverId = `id-${id}`;
  return <_StyledLink href={url || undefined} className="group" id={hoverId}>
      <_StyledDiv $_css={[{
      "position": "relative",
      "zIndex": "10",
      "height": "100%"
    }]}>
        <_StyledDiv2>
          <_StyledDiv3>
            <_StyledDiv4>
              {(productHighlights ?? []).map((highlight, i) => <_StyledTag key={i} variant="transparent-20" style={{
              transition: `opacity 250ms ease-in-out ${i * 75}ms, transform 250ms ease-in-out ${i * 75}ms`
            }} $_css2={[{
              "@media (min-width: 1024px)": {
                "--tw-translate-y": "0.4rem",
                "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))",
                "opacity": "0",
                ".group:hover &": {
                  "--tw-translate-y": "0",
                  "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))",
                  "opacity": "1"
                }
              }
            }]}>
                  {highlight}
                </_StyledTag>)}
              <IconButton as="div" icon={{
              component: ArrowRight,
              style: {
                "transitionProperty": "transform",
                "transitionDuration": "250ms",
                "transitionTimingFunction": "ease-in-out"
              }
            }} hoverId={hoverId} size="small" />
            </_StyledDiv4>
          </_StyledDiv3>
          <Asset asset={asset} imageProps={{
          ratio,
          sizes: "lg:25vw"
        }} />
        </_StyledDiv2>
        <_StyledDiv5>
          <_StyledSpan />
          <_StyledSpan2 />
          <_StyledH>{title}</_StyledH>
          <_StyledSpan3 />
        </_StyledDiv5>
        {productCaption && <_StyledDiv6>
            <_StyledP $_css3={[{
          "fontSize": "1.4rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.014000000000000002rem",
          "fontWeight": "400",
          "@media (min-width: 1024px)": {
            "fontSize": "1.8rem",
            "lineHeight": "1.5",
            "letterSpacing": "0.018rem",
            "fontWeight": "400"
          }
        }, themeStyle.label]}>
              {productCaption}
            </_StyledP>
          </_StyledDiv6>}
      </_StyledDiv>
    </_StyledLink>;
}
type Props = FragmentType<typeof fragment> & {
  mode: Mode;
  ratio?: Ratio;
};
export function ProductCard(props: Props) {
  const {
    id,
    title,
    productImage,
    url,
    productCaption,
    ...rest
  } = useFragment(fragment, props);
  const specs = useFragment(hardwareSpecsFragment, rest);
  // Fall back to the two available chip fields when product highlights are absent.
  const highlights = [specs.productFovType, specs.productVerticalFieldOfView].filter((s): s is string => Boolean(s));
  const {
    mode = "light",
    ratio
  } = props;
  const asset = takeFirst(productImage);
  return <ProductCardView asset={asset} id={id} mode={mode as Mode} productCaption={productCaption} productHighlights={highlights} ratio={ratio} title={title} url={url} />;
}
var _StyledLink = _styled(Link).withConfig({
  componentId: "sc-sf-1agcnz6-0"
})({
  "display": "block",
  "height": "100%"
});
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1agcnz6-1"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1agcnz6-2"
})({
  "position": "relative",
  "borderRadius": "4px"
});
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-1agcnz6-3"
})({
  "position": "absolute",
  "left": "0",
  "right": "0",
  "bottom": "0",
  "zIndex": "10",
  "display": "flex",
  "alignItems": "flex-end",
  "justifyContent": "flex-end",
  "gap": "var(--spacing-xs)",
  "padding": "var(--spacing-xs)"
});
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-1agcnz6-4"
})({
  "display": "flex",
  "flexWrap": "wrap",
  "alignItems": "flex-end",
  "gap": "var(--spacing-xs)"
});
var _StyledTag = _styled6(Tag).withConfig({
  componentId: "sc-sf-1agcnz6-5"
})`${p => p.$_css2}`;
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-1agcnz6-6"
})({
  "position": "relative",
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "space-between",
  "gap": "var(--spacing-md)",
  "paddingBottom": "var(--spacing-md)",
  "paddingTop": "var(--spacing-lg)",
  "@media (min-width: 1024px)": {
    "paddingTop": "var(--spacing-xl)"
  }
});
var _StyledSpan = _styled8("span").withConfig({
  componentId: "sc-sf-1agcnz6-7"
})({
  "position": "absolute",
  "bottom": "0",
  "left": "0",
  "right": "0",
  "borderTopWidth": "1px",
  "borderColor": "var(--stroke-primary)"
});
var _StyledSpan2 = _styled9("span").withConfig({
  componentId: "sc-sf-1agcnz6-8"
})({
  "position": "absolute",
  "bottom": "0",
  "left": "0",
  "right": "0",
  "height": "1px",
  "transformOrigin": "left",
  "--tw-scale-x": "0",
  "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
  "transitionProperty": "transform",
  "transitionDuration": "500ms",
  "transitionTimingFunction": "ease-in-out",
  ".group:hover &": {
    "--tw-scale-x": "1",
    "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
  }
});
var _StyledH = _styled10("h2").withConfig({
  componentId: "sc-sf-1agcnz6-9"
})({
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledSpan3 = _styled11("span").withConfig({
  componentId: "sc-sf-1agcnz6-10"
})({
  "height": "0.8rem",
  "width": "0.8rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv6 = _styled12("div").withConfig({
  componentId: "sc-sf-1agcnz6-11"
})({
  "display": "flex",
  "flexDirection": "column",
  "paddingTop": "var(--spacing-md)"
});
var _StyledP = /*#__PURE__*/_styled13("p").withConfig({
  displayName: "product-card___StyledP",
  componentId: "sc-19expal-0"
})(["", ""], p => p.$_css3);